import React from 'react';
import { Col, Form, InputNumber, Radio, Row } from 'antd';
import { I18n } from '@aws-amplify/core';
import type { RadioChangeEvent } from 'antd/lib/radio';

import { TASK_TIME_FORMAT } from '@/components/views/BeraJob/hooks/types/response';
import type { TaskTimeFormat } from '@/components/views/BeraJob/hooks/types/response';

import { toTaskTimePrecision, toWholeSeconds } from './utils';

const { useFormInstance, useWatch } = Form;

const TASK_TIME_FIELD = 'task_time';
const TASK_TIME_FORMAT_FIELD = 'task_time_format';
const MIN_TASK_TIME = 0;
const DECIMAL_SEPARATOR = ',';
const INPUT_WIDTH = '10.75rem';

interface TaskTimeProps {
	fileId: string;
}

export function TaskTime({ fileId }: Readonly<TaskTimeProps>): JSX.Element {
	const form = useFormInstance();

	const taskTimeField = [fileId, TASK_TIME_FIELD];
	const taskTimeFormatField = [fileId, TASK_TIME_FORMAT_FIELD];

	const format: TaskTimeFormat = useWatch(taskTimeFormatField, form) ?? TASK_TIME_FORMAT.SECONDS;

	const precision = toTaskTimePrecision(format);

	const options = [
		{ value: TASK_TIME_FORMAT.SECONDS, label: I18n.get('Seconds') },
		{ value: TASK_TIME_FORMAT.MINUTES, label: I18n.get('Minutes') }
	];

	function handleFormatChange({ target }: RadioChangeEvent): void {
		const value = form.getFieldValue(taskTimeField);

		if (target.value !== TASK_TIME_FORMAT.SECONDS || !value) {
			return;
		}

		const rounded = toWholeSeconds(value);

		form.setFieldValue(taskTimeField, rounded);
	}

	return (
		<Form.Item label={I18n.get('Task time')} labelCol={{ span: 24 }} required>
			<Row align="middle" gutter={[24, 8]}>
				<Col flex={INPUT_WIDTH}>
					<Form.Item
						noStyle
						name={taskTimeField}
						rules={[{ required: true, message: I18n.get('Insert a task duration') }]}
					>
						<InputNumber min={MIN_TASK_TIME} precision={precision} decimalSeparator={DECIMAL_SEPARATOR} />
					</Form.Item>
				</Col>
				<Col>
					<Form.Item noStyle name={taskTimeFormatField}>
						<Radio.Group options={options} onChange={handleFormatChange} />
					</Form.Item>
				</Col>
			</Row>
		</Form.Item>
	);
}
